import type { SelectFieldOptions } from '#layers/block-engine/public';
import { defineAsyncComponent } from 'vue';
import { selectOptionsFormSchema } from '#layers/block-engine/public';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';
import { PRIMITIVE_TYPES_NUMBER } from '#layers/data-model/constants/schema/primitive-type';
import { createPrimitiveType } from '#layers/data-model/field-types/configured-input-default';
import { defineFieldTypeTemplate } from '#layers/data-model/field-types/define';
import { getPrimitiveInputDefaults } from '#layers/data-model/field-types/primitive-input-defaults';
import { FilterOperator } from '#layers/data-model/types/filters';
import { getTypename } from '#layers/data-model/utils/schema/primitive-type';

const SelectFilterInputGroup = defineAsyncComponent(() => import('./FilterInputGroup.vue'));

const MULTIPLE_OPTION_TYPES = ['json'] as const;
const SINGLE_OPTION_TYPES = ['string',	...PRIMITIVE_TYPES_NUMBER, 'boolean', 'uuid'] as const;

export default defineFieldTypeTemplate<SelectFieldOptions>({
	type: 'primitive',
	id: 'select',
	name: '$t:field_types.select.name',
	description: '$t:field_types.select.description',
	icon: 'lucide:chevron-down',
	sort: 400,
	keywords: ['dropdown', 'enum', 'option', 'choice'],
	recommendedDisplay: BuiltinBlockIds.DisplayLabels,
	supportedPrimitiveTypes: ({ options }) => {
		if (options?.multiple === undefined) {
			return [...MULTIPLE_OPTION_TYPES, ...SINGLE_OPTION_TYPES];
		}

		return options.multiple ? MULTIPLE_OPTION_TYPES : SINGLE_OPTION_TYPES;
	},
	options: ({ form, context: { primitiveType, isNewField }, values }) => {
		const typeName = getTypename(primitiveType);
		const resolvedType = typeName !== 'json' ? primitiveType : createPrimitiveType('string');
		const valueInput = getPrimitiveInputDefaults(resolvedType);

		return form([
			{
				name: 'multiple',
				label: '$t:field_types.select.options.multiple.label',
				description: '$t:field_types.select.options.multiple.description',
				block: BuiltinBlockIds.InputSwitch,
				props: {
					disabled: !isNewField,
					// TODO: Translate this, once we have a way to translate the field type templates
					disabledNotice: 'This selection cannot be changed after creation of a field',
				},
				default: () => typeName === 'json',
			},
			{ ...selectOptionsFormSchema({ options: values.options ?? [], valueInput }), fieldProps: { layout: 'stacked' } },
		]);
	},
	filter: ({ options }) => options?.options?.length
		? {
				inputGroup: SelectFilterInputGroup,
				supportedOperators: [FilterOperator.EQ, FilterOperator.NEQ, FilterOperator.IN, FilterOperator.NIN],
			}
		: undefined,
});
